// Shared mapping for a Stripe Terminal reader "health" value returned by
// GET /systemdevice/readerstatus. Health is derived server-side from the reader
// status AND heartbeat freshness, because Stripe's "online" flag is sticky and
// lags real connectivity.

export const READER_HEALTH_META = {
  online: { label: "Online", color: "#16a34a", bg: "#f0fdf4" },
  online_stale: { label: "Stale", color: "#b45309", bg: "#fffbeb" },
  offline: { label: "Offline", color: "#dc2626", bg: "#fef2f2" },
  error: { label: "Error", color: "#dc2626", bg: "#fef2f2" },
  not_configured: { label: "Not configured", color: "#64748b", bg: "#f1f5f9" },
  not_found: { label: "Not found", color: "#64748b", bg: "#f1f5f9" },
};

export function readerHealthMeta(health) {
  return READER_HEALTH_META[health] || READER_HEALTH_META.not_configured;
}

// Format a Stripe epoch timestamp (milliseconds) into an absolute datetime plus
// a live "X seconds/minutes ago" relative string. Pass a ticking `nowMs` so the
// relative label updates each second.
export function formatEpoch(ms, nowMs) {
  if (!ms) return null;
  const epoch = Number(ms);
  const now = nowMs || Date.now();
  const date = new Date(epoch);
  const absolute = date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const diffSec = Math.max(0, Math.round((now - epoch) / 1000));
  let relative;
  if (diffSec < 60) {
    relative = `${diffSec} second${diffSec === 1 ? "" : "s"} ago`;
  } else if (diffSec < 3600) {
    const m = Math.floor(diffSec / 60);
    relative = `${m} minute${m === 1 ? "" : "s"} ago`;
  } else if (diffSec < 86400) {
    const h = Math.floor(diffSec / 3600);
    relative = `${h} hour${h === 1 ? "" : "s"} ago`;
  } else {
    const d = Math.floor(diffSec / 86400);
    relative = `${d} day${d === 1 ? "" : "s"} ago`;
  }
  return { epoch, absolute, relative, diffSec };
}

// A short human line describing the status (e.g. "Online · seen 3s ago").
export function readerHealthDetail(readerStatus) {
  if (!readerStatus) return "";
  const meta = readerHealthMeta(readerStatus.health);
  if (readerStatus.health === "online" || readerStatus.health === "online_stale") {
    const stale = readerStatus.stale_seconds;
    if (typeof stale === "number") {
      return `${meta.label} · last seen ${stale}s ago`;
    }
    return meta.label;
  }
  if (readerStatus.health === "error") {
    return readerStatus.error || meta.label;
  }
  return meta.label;
}
